import { useAuth0 } from "@auth0/auth0-react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import type { AlertColor } from "@mui/material/Alert/Alert";
import dayjs from "dayjs";
import React from "react";

import { USE_AUTH } from "../../api/apiConfig";
import type QueryClient from "../../api/query/queryClient";

const MAX_NOTE_LENGTH = 2000;

interface InsuranceArNotesPanelProps {
  entry: InsuranceArEntryMessage;
  practiceId: string;
  queryClient: QueryClient;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  notifyError: (error: unknown, fallback: string) => void;
  onRefresh: () => void;
}

export default function InsuranceArNotesPanel({
  entry,
  practiceId,
  queryClient,
  updateSnackBar,
  notifyError,
  onRefresh,
}: InsuranceArNotesPanelProps) {
  const { user } = useAuth0();
  const [draft, setDraft] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editDraft, setEditDraft] = React.useState("");
  const [confirmDeleteId, setConfirmDeleteId] = React.useState<string | null>(
    null,
  );

  const onAdd = async () => {
    const content = draft.trim();
    if (!content || saving) return;
    setSaving(true);
    try {
      await queryClient.createInsuranceArComment({
        practiceId,
        entryId: entry.id,
        content,
      });
      updateSnackBar({ severity: "success", message: "Note added." });
      setDraft("");
      onRefresh();
    } catch (error: unknown) {
      notifyError(error, "Failed to add the note.");
    }
    setSaving(false);
  };

  const onSaveEdit = async (comment: InsuranceArCommentMessage) => {
    const content = editDraft.trim();
    if (!content || saving) return;
    setSaving(true);
    try {
      await queryClient.updateInsuranceArComment({
        practiceId,
        commentId: comment.wieldyId,
        content,
      });
      updateSnackBar({ severity: "success", message: "Note updated." });
      setEditingId(null);
      onRefresh();
    } catch (error: unknown) {
      notifyError(error, "Failed to update the note.");
    }
    setSaving(false);
  };

  const onDelete = async (comment: InsuranceArCommentMessage) => {
    if (saving) return;
    setSaving(true);
    try {
      await queryClient.deleteInsuranceArComment({
        practiceId,
        commentId: comment.wieldyId,
      });
      updateSnackBar({ severity: "success", message: "Note deleted." });
      setConfirmDeleteId(null);
      onRefresh();
    } catch (error: unknown) {
      notifyError(error, "Failed to delete the note.");
    }
    setSaving(false);
  };

  return (
    <Box sx={{ padding: "16px 24px", backgroundColor: "#F9FAFB" }}>
      <Box sx={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Add a note…"
          value={draft}
          disabled={saving}
          inputProps={{ maxLength: MAX_NOTE_LENGTH }}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              onAdd();
            }
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={onAdd}
          disabled={saving || !draft.trim()}
        >
          Add
        </Button>
      </Box>

      {entry.comments.length === 0 ? (
        <Typography sx={{ fontSize: "12px", color: "#98A2B3" }}>
          No notes yet.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {entry.comments.map((comment) => {
            const isAuthor = !USE_AUTH || comment.authorId === user?.sub;
            return (
              <Box key={comment.id}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "2px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#101828",
                    }}
                  >
                    {comment.authorName ?? "Teammate"}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#667085" }}>
                    {dayjs(comment.createdAt).format("MMM D, YYYY")}
                  </Typography>
                  {isAuthor && editingId !== comment.id ? (
                    <>
                      <IconButton
                        size="small"
                        aria-label="Edit note"
                        disabled={saving}
                        onClick={() => {
                          setEditingId(comment.id);
                          setEditDraft(comment.content);
                          setConfirmDeleteId(null);
                        }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: "14px" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Delete note"
                        disabled={saving}
                        onClick={() => setConfirmDeleteId(comment.id)}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: "14px" }} />
                      </IconButton>
                    </>
                  ) : null}
                </Box>

                {editingId === comment.id ? (
                  <Box sx={{ display: "flex", gap: "8px" }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={editDraft}
                      disabled={saving}
                      inputProps={{ maxLength: MAX_NOTE_LENGTH }}
                      onChange={(event) => setEditDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          onSaveEdit(comment);
                        }
                      }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => onSaveEdit(comment)}
                      disabled={saving || !editDraft.trim()}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setEditingId(null)}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: "13px", color: "#344054" }}>
                    {comment.content}
                  </Typography>
                )}

                {confirmDeleteId === comment.id ? (
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                  >
                    <Typography sx={{ fontSize: "12px", color: "#B42318" }}>
                      Delete this note?
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      onClick={() => onDelete(comment)}
                      disabled={saving}
                    >
                      Delete
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setConfirmDeleteId(null)}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
